import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useAppContext } from "./AppContext";
import { SelectorOption } from "./GenerateSelectorOptions";
import { parseString, reconstructQueryString } from "./ParseString";
import { HStack, Text, useColorMode, useTheme } from "@chakra-ui/react";
import { HistoryIconSVG } from "./svg/HistoryIconSVG";

interface MenuProps {
  isMenuOpen: boolean;
  setIsMenuOpen: (isMenuOpen: boolean) => void;
  onClickfocus: () => void;
  options: SelectorOption[];
  isHistory?: boolean;
  handleChange: (e: any) => void;
}

export const DropDown: React.FC<MenuProps> = memo(
  ({ isMenuOpen, setIsMenuOpen, options, onClickfocus, isHistory, handleChange }): JSX.Element => {
    const { query, versions, handleSubmit } = useAppContext();

    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    const { colorMode } = useColorMode();
    const theme = useTheme();
    const isDarkMode = colorMode === "dark";

    const handleMenuClick = useCallback(
      (option: SelectorOption): void => {
        const { chapter, verseStart, verseEnd } = parseString(query);
        const newBookName = option.value;
        const newQuery = reconstructQueryString(query, newBookName, chapter, verseStart, verseEnd, versions);

        handleChange({ target: { value: newQuery } });
        setIsMenuOpen(false);
        onClickfocus();
        isHistory && handleSubmit(versions, newQuery);
      },
      [query, setIsMenuOpen, onClickfocus, versions, handleSubmit, handleChange, isHistory]
    );

    // Handle keyboard navigation
    const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
    const menuRef = useRef<HTMLUListElement | null>(null);

    useEffect(() => {
      // Do not add event listener if menu is not open
      if (!isMenuOpen || options.length === 0) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "ArrowDown") {
          setActiveIndex((prev) => (prev === null || prev === options.length - 1 ? 0 : prev + 1));
        } else if (e.key === "ArrowUp") {
          setActiveIndex((prev) => (prev === null || prev === 0 ? options.length - 1 : prev - 1));
        } else if (e.key === "Enter" && activeIndex !== null) {
          handleMenuClick(options[activeIndex]);
        }

        // Ensure active (focused) option is visible inside the overflowed menu
        if (activeIndex !== null && menuRef.current && optionRefs.current[activeIndex]) {
          const menu = menuRef.current;
          const option = optionRefs.current[activeIndex];

          if (!option) return;
          const scrollPadding = 20;

          if (option.offsetTop + option.clientHeight + scrollPadding > menu.scrollTop + menu.clientHeight) {
            // If option is below the visible area
            const toCenterDistance = option.offsetTop;
            menu.scrollTop = toCenterDistance;
          } else if (option.offsetTop < menu.scrollTop) {
            // If option is above the visible area
            menu.scrollTop = option.offsetTop;
          }
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [options, activeIndex, handleMenuClick, isMenuOpen]);

    return (
      <>
        {isMenuOpen && options.length > 0 && (
          <ul
            ref={menuRef}
            style={{
              position: "absolute",
              backgroundColor: isDarkMode ? theme.colors.gray[800] : "white",
              border: `1px solid ${isDarkMode ? theme.colors.gray[600] : "#ccc"}`,
              width: "100%",
              maxHeight: "136px",
              overflowY: "auto",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              marginTop: "45px",
              listStyleType: "none",
              padding: "8px 0",
              zIndex: 100,
              borderRadius: "8px",
            }}
          >
            {options.map((option, idx) => (
              <li
                key={idx}
                ref={(el) => (optionRefs.current[idx] = el)}
                style={{
                  marginLeft: "8px",
                  padding: "8px",
                  cursor: "pointer",
                  borderRadius: "4px", // Slight rounding for each item
                  transition: "background-color 0.2s", // Smooth transition for hover effect
                  backgroundColor:
                    idx === activeIndex
                      ? colorMode === "dark"
                        ? theme.colors.gray[700]
                        : theme.colors.gray[200]
                      : "transparent",
                }}
                onMouseDown={(e) => {
                  handleMenuClick(option);
                }}
                onMouseOver={(e) => {
                  // Change background color on hover
                  e.currentTarget.style.backgroundColor = isDarkMode ? theme.colors.gray[500] : theme.colors.gray[100];
                }}
                onMouseOut={(e) => {
                  // Reset background color after hover
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <HStack>
                  {isHistory && <HistoryIconSVG color={isDarkMode ? "white" : "black"} />}
                  <Text>{option.value}</Text>
                </HStack>
              </li>
            ))}
          </ul>
        )}
      </>
    );
  }
);
